import { getToken, clearToken } from "@/lib/auth";
import { normalizeUploadResponse, type UploadResult } from "@/lib/upload-errors";

const API_BASE = (import.meta.env.VITE_API_URL ?? "") + "/api";

const headers = () => {
  const token = getToken();
  return { Authorization: token ? `Bearer ${token}` : "" };
};

type ApiErrorHandler = (message: string, status: number) => void;
let _onApiError: ApiErrorHandler | null = null;

export function setApiErrorHandler(fn: ApiErrorHandler) {
  _onApiError = fn;
}

export class ApiError extends Error {
  // `code` carries the backend's stable machine-readable error key when the
  // endpoint returns a structured detail, so callers branch on it instead of
  // pattern-matching the human message.
  constructor(message: string, readonly status: number, readonly code?: string) {
    super(message);
    this.name = "ApiError";
  }
}

function _extractHttpError(body: Record<string, unknown>, fallback: string): string {
  // FastAPI HTTPException  → { detail: string }
  // Pipeline 500 response → { detail: string } or { error: string }
  if (typeof body.detail === "string") return body.detail;
  if (body.detail && typeof body.detail === "object") {
    const d = body.detail as Record<string, unknown>;
    if (d.error === "missing_required_columns" && Array.isArray(d.missing)) {
      return `Required columns not found: ${(d.missing as string[]).join(", ")}`;
    }
    if (d.error === "book_data_exists") {
      return "This portfolio already has data loaded. Delete the previous run before loading it again.";
    }
    if (d.error === "destructive_mapping_write" && Array.isArray(d.losses)) {
      return `This change would discard existing mapping configuration: ${(d.losses as string[]).join("; ")}. Reload the page so you are editing the current config, or contact an admin to remove it deliberately.`;
    }
    if (d.error === "missing_mapped_fields" && Array.isArray(d.missing)) {
      return `The book's mapping does not provide fields this facility's rules need: ${(d.missing as string[]).join(", ")}. Add them in the book's Mapping tab before activating.`;
    }
    if (typeof d.message === "string") return d.message;
    if (typeof d.msg === "string") return d.msg;
  }
  if (typeof body.error === "string") return body.error;
  return fallback;
}


import { cacheGet, cacheSet, cacheInvalidate, getInFlight, setInFlight, deleteInFlight, TTL } from "./apiCache";
import type {
  FacilityInfo, FacilityListItem, Metrics, Analytics, WaterfallRunRecord, WaterfallRunsResponse,
  CovenantObservationsResponse, IncidentRecord, IncidentStatus, IncidentDetail,
  IncidentsSummary, EligibilityViews, AcquisitionCohortsResponse, FacilityGlanceResponse, LineMovementsResponse, NoteLedger,
  DpdStratificationResponse, AssetTestResponse, PeriodCostResponse,
  ReconAliasInfo, ReconMatch, ReconOpenPayment, ReconQueueResponse, ReconSummaryResponse,
  LedgerResponse, LedgerOpenItemsResponse, LedgerPeriod, MonthlyReport,
} from "@/pages/facility/facility.types";

import type { BorrowerProfileInput, BorrowerProfileResponse } from "@/types/borrowerProfile";
import type { BorrowerFinancialsResponse, FinancialDocumentRecord } from "@/types/borrowerFinancials";

// Re-exported for consumers that reach incidents through the API client
// (IncidentsInbox, IncidentDetailModal, useGlobalStore).
export type { IncidentRecord, IncidentStatus, IncidentDetail, IncidentsSummary } from "@/pages/facility/facility.types";

/** A borrower row as `GET /borrowers` returns it. `book` is null until one is
 *  onboarded; every figure inside it comes from that book's latest snapshot. */
export type BorrowerListEntry = {
  id: string;
  legal_name: string;
  country: string | null;
  jurisdiction: string | null;
  currency: string;
  kyc_status: "PENDING" | "APPROVED" | "REJECTED";
  facility_count: number;
  /** Contracts, not facilities. The two travel side by side and count
   *  different things since G2 gave the Deal its own row. */
  deal_count?: number;
  has_active_book: boolean;
  /** Whether a presentation card exists. The document itself never travels in
   *  the list — same rule as `has_logo`. */
  has_profile?: boolean;
  book: {
    id: string;
    currency: string;
    asset_class: string | null;
    asset_class_label: string | null;
    snapshot_date: string | null;
    outstanding: number | null;
    disbursed: number | null;
    write_off_rate: number | null;
    par_buckets: Record<string, number> | null;
    /** Live loans that reported no DPD — outside the base of `par_buckets`. */
    missing_dpd?: { loans: number; outstanding: number } | null;
    loan_count: number | null;
    last_run: { status: string; pipeline_type: string | null; started_at: string | null } | null;
  } | null;
};

/* ── The Deal: the contract 1-N facilities live under ──────────────────
 *
 * Transcribed from `servicing_engine/api/deals.py`, which is the contract.
 * `tests/test_wire_shape_baseline.py` pins these key sets against the running
 * API, so a key renamed on one side fails there instead of rendering
 * `undefined` on screen.
 */

/** `deal_config` — the contract's term sheet. Four known blocks, validated
 *  server-side (`schemas/deal_config.py`), plus whatever else the term sheet
 *  carries. `GET /deals/{id}` serves it twice: as stored and as effective,
 *  with the defaults that live in code resolved. */
export type DealConfig = {
  /** When the contract pays: the only calendar it has. `determination` is the
   *  name it was stored under before, folded into this one on read, and kept
   *  in the type because a stored config written then still carries it. */
  payment?: { frequency: "monthly" | "quarterly"; day: number } | null;
  determination?: { frequency: "monthly" | "quarterly"; day: number } | null;
  reconciliation?: { shortfall_threshold_pct: number };
  covenant_defaults?: { cure_days: number | null; on_breach: string };
  credits?: { allocation: "none" | "pro_rata_obligation" | "fixed_shares"; shares?: Record<string, number> };
  /** The Pricing Annex the contract signed. Absent until somebody declares it:
   *  there is no default, and the platform fee is then pending, not zero. */
  pricing_annex?: string;
  /** The contract's Priority of Payments: the ladder the Payment Report fills. */
  waterfall?: { steps: DealLadderStep[] };
  [key: string]: unknown;
};

/** One rung of `deal_config.waterfall`, as stored. Exactly one source: the
 *  facility categories it `collects`, a Deal-level `step`, or neither — a
 *  declared line named by its `label`, with an `amount` or without one (then
 *  it is reported pending, never zero). */
export type DealLadderStep = {
  priority: number;
  key?: string | null;
  label?: string | null;
  recipient?: string | null;
  collects?: string[] | null;
  step?: string | null;
  amount?: number | null;
};

/** One Pricing Annex as `GET /deals/{id}/config-options` offers it to THIS
 *  contract: `available` false comes with the reason the write would give. */
export type DealPricingAnnexOption = {
  version: string;
  description: string;
  currency: string;
  fee_day_count: string;
  /** The contract an annex was negotiated for, when it was for one. */
  exclusive_to: string | null;
  available: boolean;
  refusal: string | null;
};

/** What the contract's config may say: the annexes it may sign and the
 *  vocabulary of a rung. Served so the editor offers what the PUT accepts. */
export type DealConfigOptions = {
  deal_id: string;
  deal_reference: string;
  pricing_annex: string | null;
  pricing_annexes: DealPricingAnnexOption[];
  ladder: {
    /** The facility categories a rung may sum. */
    collects: string[];
    /** The kinds charged on the contract as a whole. */
    steps: string[];
  };
};

/** A row of `GET /deals`. No config and no facilities — the list is a list.
 *  `status` is derived from the live facilities («≥1 ACTIVE ⇒ ACTIVE»), it is
 *  not a column, and nothing here is a sum. */
export type DealListItem = {
  id: string;
  deal_reference: string;
  deal_name: string;
  book_id: string;
  borrower_id: string;
  borrower: { id: string; legal_name: string; country: string | null; kyc_status: string } | null;
  currency: string;
  status: "ACTIVE" | "DRAFT";
  facility_count: number;
  /** Null under an anonymous grant: an email domain is a name by another route. */
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  /** True when this payload was served under an anonymous grant (HU-066). */
  anonymized?: boolean;
};

/** One facility as the Deal's detail lists it. Lighter than `FacilityInfo`:
 *  enough to name it, say what it is and link to its cockpit.
 *
 *  `borrowing_base_cockpit` is the latest `daily_metrics` figure — the one the
 *  cockpit shows. The attested pool snapshot now measures the same population
 *  and agrees to the cent; the key stays explicit about which row it is read
 *  from. The Deal still adds nothing up, because its facilities can be sealed
 *  in different currencies. */
export type DealFacilityStub = {
  id: string;
  facility_reference: string;
  facility_name: string;
  deal_id: string;
  book_id: string;
  status: string;
  currency: string;
  facility_type: string;
  max_facility_size: number | null;
  note_balance: number | null;
  borrowing_base_cockpit: number | null;
  borrowing_base_cockpit_date: string | null;
  updated_at: string | null;
};

/** `GET /deals/{id}`: the list row plus the config (twice) and the live
 *  facilities. Both configs come back null under an anonymous grant. */
export type DealInfo = DealListItem & {
  deal_config: DealConfig | null;
  deal_config_effective: DealConfig | null;
  facilities: DealFacilityStub[];
};

/** One column of a rung, or of the report as a whole.
 *
 *  `amount` is the sum of what IS known and `complete` says whether anything is
 *  missing from it. They travel together on purpose: a partial sum with no mark
 *  reads as a complete one, and `missing` names which facility could not price
 *  which category and why, so a reader can tell a gap that matters from one
 *  that does not. */
export type LadderColumn = {
  amount: number | null;
  complete: boolean;
  missing: {
    facility_id: string | null;
    facility_reference: string | null;
    category: string | null;
    reason: string | null;
  }[];
};

/** One rung of the contract's Priority of Payments.
 *
 *  `source` says what the figure rests on: summed from the facilities, charged
 *  on the contract, typed into its config, or unknown. `RESIDUAL` is the excess
 *  line, which carries no amount in either column — the report states what is
 *  owed and never looks at the cash that came in. */
export type LadderRung = {
  priority: number;
  key: string | null;
  label: string | null;
  recipient: string | null;
  source: "FACILITIES" | "DEAL" | "DECLARED" | "UNKNOWN" | "RESIDUAL" | "INCLUDED";
  accrued: LadderColumn;
  projected: LadderColumn;
  /** One line per facility that contributes, plus — on the rung that carries
   *  the platform fee — one with no facility: the Series' monthly minimum,
   *  which is the contract's and no facility's. A line with both a figure and
   *  a gap is priced in part: the figure is the known part. */
  breakdown: {
    facility_id: string | null;
    facility_reference: string | null;
    /** The run this facility's figure was read from. Null on the contract's
     *  own line (the Series' monthly minimum). */
    metric_date?: string | null;
    category: string;
    label?: string | null;
    accrued: number | null;
    projected: number | null;
    accrued_gap: string | null;
    projected_gap: string | null;
  }[];
  note: string | null;
};

/** One column of the Series' monthly minimum: the top-up it adds to the
 *  platform fee rung. `amount` is null when it cannot be known — a facility's
 *  fees are unknown and the known ones do not reach the floor. */
export type SeriesMinimumColumn = {
  /** In the annex's currency (`currency`, USD). */
  monthly_minimum: number | null;
  draw_fee_monthly_minimum: number | null;
  currency?: string;
  /** Contract's currency per unit of the annex's, at the report's rate. */
  annex_rate?: number | null;
  /** Share of the period elapsed: the accrued floor is prorated by it. */
  fraction?: number | null;
  /** The floors as charged in this column: in the contract's currency, prorated. */
  monthly_minimum_charged?: number | null;
  draw_fee_monthly_minimum_charged?: number | null;
  lines: number;
  lines_with_balance_out: number;
  series_fees: number;
  series_fees_complete: boolean;
  amount: number | null;
  applies: boolean;
  gap: string | null;
};

/** `GET /deals/{id}/period-cost` — the Payment Report.
 *
 *  Every amount is in `currency`, the contract's, converted at the rate of
 *  `as_of` for both columns. `projected_to` is null when the contract declares
 *  no payment calendar, and `projection_gap` says so. */
/** Which run one facility's figures on the Payment Report come from. */
export type LadderMeasurement = {
  facility_id: string;
  facility_reference: string;
  metric_date: string | null;
  figures_as_of: string | null;
  exact: boolean;
  pipeline_status: string | null;
};

export type DealPeriodLadder = {
  deal_id: string;
  deal_reference: string;
  currency: string;
  /** The date the figures were measured on: the runs each facility recorded,
   *  read with the facility's own rule. */
  as_of: string;
  /** The date that was asked. */
  requested_as_of?: string;
  /** Whether every figure is that date's own run. A date with no run of its
   *  own is read with the last run before it. */
  exact?: boolean;
  /** False when the facilities were measured on different days: the rungs and
   *  the totals are then incomplete, and `period_alignment_gap` says which
   *  facility was measured when. */
  period_aligned?: boolean;
  period_alignment_gap?: string | null;
  measurements?: LadderMeasurement[];
  period_start: string | null;
  projected_to: string | null;
  projection_gap: string | null;
  facility_count: number;
  excluded_facilities: { facility_id: string; facility_reference: string; reason: string }[];
  /** The Series' monthly minimum, worked out once over every facility. Null
   *  when the contract's annex has no monthly floor. */
  series_minimum?: {
    annex: string;
    monthly_minimum: number | null;
    draw_fee_monthly_minimum: number | null;
    accrued: SeriesMinimumColumn | null;
    projected: SeriesMinimumColumn | null;
    note: string;
  } | null;
  rungs: LadderRung[];
  accrued: { total: number | null; complete: boolean; unpriced: (string | null)[] };
  projected: { total: number | null; complete: boolean; unpriced: (string | null)[] };
  anonymized?: boolean;
};

/** An entry of `GET /deals/{id}/audit`. The contract's own rows and its
 *  facilities', told apart by `scope`; the facility fields are null on a
 *  contract-scoped entry so the shape is one. */
export type DealAuditEntry = {
  id: string;
  scope: "deal" | "facility";
  facility_id: string | null;
  facility_reference: string | null;
  config_type: string;
  action: string | null;
  previous_value: unknown;
  new_value: unknown;
  changed_by: string | null;
  created_at: string | null;
};

/** One live grant that reaches a Deal: written against it (`scope: "deal"`)
 *  or the book's `deals_view` wildcard (`scope: "book"`). */
export type DealAccessGrant = {
  id: string;
  email: string;
  permission: string;
  scope: "deal" | "book";
  deal_id: string | null;
  anonymized: boolean;
  granted_by: string | null;
  granted_at: string | null;
};

/** `GET /deals/{id}/access` — who reads this contract, and therefore who will
 *  read the next facility added to it without anyone granting anything. The
 *  facility alta shows it before confirming: the mitigation is visibility, not
 *  a block. */
export type DealAccess = { deal_id: string; count: number; grants: DealAccessGrant[] };

/* ── The Deal's two read-models: the covenant rollup and the aggregate ──
 *
 * Both are computed on read and stored nowhere. Transcribed from
 * `services/covenant_rollup.py` and `services/deal_aggregate.py`, and pinned
 * key by key in `tests/test_wire_shape_baseline.py`.
 *
 * The shapes look verbose on purpose, and the verbosity is the contract: every
 * figure travels with the denominator that qualifies it. A screen that reads
 * only the number is the failure mode these payloads were built to prevent.
 */

/** One facility inside a metric group: the measurement, the threshold it was
 *  measured against and the margin. These live here — one level below the
 *  group — because they belong to that facility's contract and to no other. */
export type DealCovenantFacility = {
  facility_id: string;
  facility_reference: string;
  facility_name: string;
  /** Every rule this facility puts on the metric, in the contract's own
   *  language and untranslated. Empty under an anonymous grant. */
  rule_names: string[];
  /** The name of the rule whose observation governs (the worst). Null when the
   *  facility defines the metric and has not been measured, and null under an
   *  anonymous grant. */
  covenant_key: string | null;
  rules_observed: number;
  status: DealCovenantStatus;
  measured_value: number | null;
  threshold: number | null;
  headroom: number | null;
  /** Why `threshold` is that one, on a covenant with stepped thresholds
   *  («1.40x because bb_exit_rate is 10.14% (> 8%) since …»); null otherwise. */
  threshold_reason: string | null;
  determination_date: string | null;
};

/** The four verdicts a facility entry or a group can carry.
 *  `UNABLE_TO_EVALUATE` is **not** a zero and not a degree of compliance: it is
 *  a metric the contract owes with nothing measured at `as_of`. It is not on
 *  the BREACHED > WARNING > COMPLIANT ladder at all. */
export type DealCovenantStatus = "BREACHED" | "WARNING" | "COMPLIANT" | "UNABLE_TO_EVALUATE";

/** A metric group. It carries the verdict, the denominator and the worst
 *  headroom — and **no measure, no threshold and no headroom of its own**.
 *  Averaging the thresholds of two contracts fabricates a number that is in
 *  neither, so what aggregates is the verdict and the count. */
export type DealCovenantMetric = {
  metric: string;
  /** The catalog's description, in English: the metric vocabulary is ours. */
  label: string;
  status: DealCovenantStatus;
  /** Facilities of the contract that DEFINE the metric — what the contract
   *  owes, not what a pipeline produced. */
  defining_count: number;
  /** Of those, how many were measured at `as_of`. */
  evaluated_count: number;
  breached_count: number;
  status_counts: Record<DealCovenantStatus, number>;
  /** The worst margin, **with the facility it belongs to inside it**, so it
   *  cannot be read as a headroom of the Deal. Null when nothing was measured. */
  worst_headroom: { value: number; facility_id: string; facility_reference: string } | null;
  facilities: DealCovenantFacility[];
};

/** `GET /deals/{id}/covenants`. There is no key here for "the Deal is
 *  breached", and there must not be one: a verdict belongs to a metric. */
export type DealCovenants = {
  deal_id: string;
  as_of: string;
  /** The launched facilities — the denominator. A DRAFT is a proposal and is
   *  never counted; `non_active_count` says how many the contract holds. */
  facility_count: number;
  non_active_count: number;
  metric_count: number;
  metrics: DealCovenantMetric[];
};

/** The one quote the whole aggregate uses: the Deal's currency against the
 *  book's, at the report date. `available: false` means the converted figures
 *  are null — never zero — and `reason` names the pair and the window. */
export type DealAggregateFx = {
  available: boolean;
  is_identity: boolean;
  /** Units of `native_currency` per 1 unit of `currency`. */
  rate: number | null;
  rate_date: string | null;
  source: string | null;
  reason: string | null;
};

/** The contract's borrowing base: an object and not a number, because it is
 *  the one amount a facility may legitimately not have. `status_counts` adds up
 *  to `facility_count`, so no facility falls out of the picture unseen. */
export type DealAggregateBorrowingBase = {
  value: number | null;
  contributing_count: number;
  status_counts: Record<DealBorrowingBaseStatus, number>;
};

/** Why a facility's borrowing base is, or is not, in the total.
 *  `NOT_APPLICABLE`: a securitization's note is already funded, so there is no
 *  base — null, never zero. `NOT_SEALED`: the run converted and sealed no
 *  native base, so its column is in the wrong currency to add. */
export type DealBorrowingBaseStatus =
  | "INCLUDED" | "NOT_APPLICABLE" | "NOT_SEALED" | "UNABLE_TO_EVALUATE";

/** Amounts by category and counts. Not one ratio: the denominators of
 *  `oc_ratio` and `coverage_ratio` are the obligation and the pool of
 *  different contracts. Null throughout when nothing contributed — zero is the
 *  answer to "the pools are empty", not to "nobody has run". */
export type DealAggregateTotals = {
  financed_outstanding: number | null;
  eligible_amount: number | null;
  financed_loan_count: number | null;
  loan_count_eligible: number | null;
  borrowing_base: DealAggregateBorrowingBase;
};

/** One facility's contribution. `*_native` is the book-currency figure its run
 *  captured before converting; the unprefixed pair is that figure converted
 *  once, at the aggregate's single rate. `currency` is the facility's own and
 *  is neither of the two — it is here so it is visible that it is not the axis. */
export type DealAggregateFacility = {
  facility_id: string;
  facility_reference: string;
  facility_name: string;
  currency: string;
  status: "MEASURED" | "UNABLE_TO_EVALUATE";
  metric_date: string | null;
  native_sealed: boolean;
  financed_outstanding_native: number | null;
  eligible_amount_native: number | null;
  financed_outstanding: number | null;
  eligible_amount: number | null;
  financed_loan_count: number | null;
  loan_count_eligible: number | null;
  borrowing_base_status: DealBorrowingBaseStatus;
  borrowing_base_native: number | null;
  borrowing_base: number | null;
};

/** `GET /deals/{id}/aggregate`. `currency` is the contract's;
 *  `native_currency` is the book's, which is what every figure was summed in
 *  before the single conversion. */
export type DealAggregate = {
  deal_id: string;
  as_of: string;
  currency: string;
  native_currency: string;
  fx: DealAggregateFx;
  /** The launched facilities — the denominator. A DRAFT is never counted;
   *  `non_active_count` says how many the contract holds besides them. */
  facility_count: number;
  non_active_count: number;
  contributing_count: number;
  complete: boolean;
  /** False when the contributing runs are not all of one date. The rate is
   *  still one, so the total reproduces; what it is not is one snapshot. */
  period_aligned: boolean;
  metric_dates: string[];
  totals: DealAggregateTotals;
  facilities: DealAggregateFacility[];
  /** The outer of the two nested limits. Absent on a payload from before it
   *  existed. */
  limits?: DealContractLimits;
  /** The reserve requirement. Absent on a payload from before it existed. */
  dsra?: DealDsra;
};

/** What the contract's Debt Service Reserve Account should hold: N months of
 *  interest — the line's and the notes' — looking FORWARD from today at the
 *  balances outstanding, clipped at each facility's maturity.
 *
 *  Informative. It is not a rung of the Payment Report, it is not compared
 *  against a bank balance and nothing funds it: an amount on an invoice says
 *  somebody has to pay it.
 *
 *  `required` is null when the contract declares no `months`, or when a
 *  facility could not be sized — `pending` names them. A total missing a
 *  facility would read as a SMALLER reserve, which is the wrong direction to be
 *  wrong in for a buffer. */
export type DealDsra = {
  months: number | null;
  currency: string;
  required: number | null;
  facilities: {
    facility_id: string;
    facility_reference: string;
    currency: string;
    line_interest: number | null;
    note_interest: number | null;
    required: number | null;
    window: { from: string; to: string } | null;
    gap: string | null;
  }[];
  pending: { facility_id: string; facility_reference: string; gap: string | null }[];
  excluded_currencies: { facility_id: string; facility_reference: string; currency: string }[];
  gap: string | null;
};

/** The contract's ceiling, what is against it and what is left, in the
 *  CONTRACT's own currency — never converted, because a programme size is a
 *  term somebody signed and not a measurement.
 *
 *  `drawn` and `available` are null, not zero, when a facility reports in
 *  another currency: adding it would need a rate that may not exist for the
 *  date, and the ceiling is then reported unenforceable rather than enforced on
 *  a figure that is missing a facility. `excluded_facilities` names them. */
export type DealContractLimits = {
  maximum: number | null;
  drawn: number | null;
  available: number | null;
  /** Σ of the launched facilities' commitments. A DRAFT counts for nothing. */
  committed: number;
  currency: string;
  excluded_facilities: { facility_id: string; facility_reference: string; currency: string }[];
};

/** One facility's share of the contract's cash. `available_funds` is what its
 *  cascade may distribute this period: with a single live facility every credit
 *  of the account, with two or more only what the tape attributes to it. Null
 *  while no statement reaches the period — unknown, never zero. */
export type DealCashFacility = {
  facility_id: string;
  facility_reference: string;
  facility_name: string;
  status: string;
  available_funds: number | null;
};

/** `GET /deals/{id}/cash` — the contract's cash of its latest distribution
 *  period and whose it is. Amounts are in the BOOK's currency, the one the
 *  accounts move; a reader that reports the contract in another converts them
 *  once, as the aggregate does.
 *
 *  `residual` is the contract's own line: credits with no confirmed match, and
 *  credits matched to a loan pledged to nobody. It funds no cascade — not even
 *  the one of the facility whose account it landed on — and it is returned
 *  whatever the covenants say. With a single live facility it is zero by
 *  definition, and `unattributed` is the other number: the cash nobody has
 *  identified, which is what the residual would become the day a second
 *  facility goes live. */
export type DealCash = {
  deal_id: string;
  as_of: string;
  currency: string | null;
  period_start: string | null;
  period_end: string | null;
  period_source: "waterfall_run" | "statement" | null;
  /** False when the runs that closed this period do not share a start. */
  period_aligned: boolean;
  has_statement: boolean;
  attribution_mode: "sole_facility" | "by_pledge" | null;
  account_count: number;
  credits_total: number | null;
  attributed_total: number | null;
  residual: number | null;
  unattributed: number | null;
  facilities: DealCashFacility[];
};

/** The period the distributions and the cash are read over: the latest close
 *  of the contract's waterfall runs at `as_of` (`source: "waterfall_run"`), or
 *  the statement itself when nothing has run yet. `aligned` is false when the
 *  runs that share the close do not share their start — published, never
 *  smoothed. */
export type DealDistributionsPeriod = {
  start: string | null;
  end: string | null;
  source: "waterfall_run" | "statement" | null;
  aligned: boolean;
};

/** The contract's cash of the period, each figure next to its converted self.
 *  `*_native` is in the book's currency (the one the accounts move), the
 *  unprefixed one in the contract's, at the one rate. Null throughout while no
 *  statement reaches the period — unknown, never zero.
 *
 *  `residual` is the line the contract owes its reader: the cash of the
 *  account is NOT the sum of the facilities' `available_funds`, and the
 *  difference is this — free-pool payments, recoveries, fees, unidentified
 *  credits. It funds no cascade and is returned whatever the covenants say. */
export type DealDistributionsCash = {
  has_statement: boolean;
  attribution_mode: "sole_facility" | "by_pledge" | null;
  account_count: number;
  credits_total_native: number | null;
  credits_total: number | null;
  attributed_total_native: number | null;
  attributed_total: number | null;
  residual_native: number | null;
  residual: number | null;
  unattributed_native: number | null;
  unattributed: number | null;
};

/** Why a facility's shortfall and excess are, or are not, in the contract's
 *  total. `NOT_SEALED`: the run converted and carries no book-currency twin
 *  for those two — a row nobody sealed — so they cannot be added. */
export type DealSealedStatus = "INCLUDED" | "NOT_SEALED" | "UNABLE_TO_EVALUATE";

/** The contract's shortfall, or its excess: an object and not a number, for
 *  the reason the aggregate's borrowing base is one. `status_counts` adds up to
 *  `facility_count`. The two are published side by side and NEVER netted — a
 *  surplus in one pool cannot cure a deficit in another. */
export type DealDistributionsSealedTotal = {
  value_native: number | null;
  value: number | null;
  contributing_count: number;
  status_counts: Record<DealSealedStatus, number>;
};

/** One line of the contract's distributions, by category — Debita's own
 *  vocabulary. `recipients` are the term sheet's payees of every facility that
 *  paid this category, carried as a list and never the axis: two facilities
 *  paying investor interest to two investors are ONE line with both names. */
export type DealDistributionsCategory = {
  category: string;
  recipients: string[];
  facility_count: number;
  amount_native: number | null;
  amount: number | null;
};

/** The sum of the cascades. `allocated` is what the facilities distributed;
 *  `shortfall` and `excess` travel with their own census. There is no `net`,
 *  and nothing here is by priority: priority 1 of a warehouse and priority 1
 *  of a securitization are different claims of different creditors. */
export type DealDistributionsBlock = {
  contributing_count: number;
  status_counts: Record<"INCLUDED" | "UNABLE_TO_EVALUATE", number>;
  /** How each facility's run knew its funds. A `PROJECTED` run had no
   *  statement behind it and measured nothing, so it does not contribute. */
  funds_source_counts: Record<"BANK" | "BANK_ATTRIBUTED" | "PROJECTED", number>;
  cash_trap_count: number;
  allocated_native: number | null;
  allocated: number | null;
  shortfall: DealDistributionsSealedTotal;
  excess: DealDistributionsSealedTotal;
  by_category: DealDistributionsCategory[];
};

/** One facility of the contract: its share of the cash and what its cascade
 *  did with it. `available_funds` comes from the cash split; the rest from its
 *  waterfall run at the contract's period close — exactly that close, never
 *  its most recent run. `by_category` here carries no `facility_count`. */
export type DealDistributionsFacility = {
  facility_id: string;
  facility_reference: string;
  facility_name: string;
  currency: string;
  status: string;
  available_funds_native: number | null;
  available_funds: number | null;
  period_end: string | null;
  funds_source: "BANK" | "BANK_ATTRIBUTED" | "PROJECTED" | null;
  cash_trap_active: boolean | null;
  excess_destination: "ORIGINATOR" | "RETAINED" | null;
  distribution_status: "INCLUDED" | "UNABLE_TO_EVALUATE";
  allocated_native: number | null;
  allocated: number | null;
  sealed_status: DealSealedStatus;
  shortfall_native: number | null;
  shortfall: number | null;
  excess_native: number | null;
  excess: number | null;
  by_category: Omit<DealDistributionsCategory, "facility_count">[];
};

/** `GET /deals/{id}/distributions` — the contract's cash of the period and the
 *  sum of what its facilities distributed, in the contract's currency. The
 *  cash is `GET /deals/{id}/cash` consumed and converted once; the
 *  distributions are the runs of the same period close, summed by category.
 *  `facility_count` is the launched facilities — never a DRAFT — and
 *  `non_active_count` how many DRAFTs the contract holds besides them. */
export type DealDistributions = {
  deal_id: string;
  as_of: string;
  currency: string;
  native_currency: string;
  fx: DealAggregateFx;
  period: DealDistributionsPeriod;
  facility_count: number;
  non_active_count: number;
  cash: DealDistributionsCash;
  distributions: DealDistributionsBlock;
  facilities: DealDistributionsFacility[];
};

/** One facility of `POST /deals` — the alta creates the contract and at least
 *  one facility in a single transaction, because a Deal is never empty. */
export type NewDealFacility = {
  facility_name: string;
  facility_reference: string;
  currency?: string;
  facility_type?: string;
  max_facility_size?: number;
  note_balance?: number;
  note_rate?: number;
  disbursement_date?: string;
  maturity_date?: string;
  reinvestment_end_date?: string;
  advance_rate?: number;
  /** Values the ORIGINATOR writes in the tape's pledged_to column to mean this
   *  facility. Its own code, which need not be the facility_reference. The only
   *  thing consulted at ingest, so a facility declaring none claims no loans —
   *  activation refuses it rather than letting the pool come out empty. */
  pledge_identifiers?: string[];
};

export type FacilitySummaryEntry = {
  id: string;
  facility_reference: string;
  facility_name: string;
  borrower: { id: string; legal_name: string; country: string | null; kyc_status: string } | null;
  book_id: string | null;
  /** The contract this facility lives under. */
  deal_id: string;
  /** Figures from the book's latest tape snapshot (absent when no tape is loaded). */
  book_loan_count?: number;
  book_outstanding?: number | null;
  book_snapshot_date?: string;
  status: string;
  currency: string | null;          // the facility's currency: unit of `metrics`/`history` money figures (HU-067.1)
  book_currency?: string | null;    // book currency: unit of book_outstanding and of analytics sections
  facility_type: string;
  max_facility_size: number | null;
  current_advance_rate: number | null;
  current_utilisation: number | null;
  updated_at: string | null;
  metrics: Metrics | null;
  alert_count: number;
  history: Metrics[];
};

export type OverviewSeriesBook = {
  book_id: string;
  currency: string;
  points: Array<{ date: string; outstanding: number; loans: number }>;
};

export type OverviewSeriesResponse = { books: OverviewSeriesBook[] };

export type DpdBucketKey = "current" | "dpd_1_30" | "dpd_31_60" | "dpd_61_90" | "dpd_90_plus";

export type OverviewAnalyticsResponse = {
  /** People with a live loan, summed across books. Each book resolves through
   *  schemas/debtors.py: the operator's stated figure when there is one, the
   *  tape's own count otherwise. */
  unique_debtors: number;
  /** Books that contributed a figure meaning people. */
  debtor_books: number;
  /** Books left out: no debtor ID in the tape, or one id per loan, where the
   *  count would be operations rather than people. */
  debtor_books_excluded: number;
  /** Raw per (book, month, asset type) — the frontend picks the dimension. */
  originations: Array<{ month: string; book_id: string; currency: string; asset_type: string | null; volume: number; loans: number }>;
  /** Daily resolution by exact origination date — feeds the loans-under-analytics hero. */
  originations_daily: Array<{ date: string; book_id: string; currency: string; asset_type: string | null; loans: number; volume: number }>;
  collections: Array<{ month: string; book_id: string; currency: string; principal: number; income: number }>;
  delinquency: Array<{ book_id: string; currency: string; date: string; bucket: DpdBucketKey; outstanding: number }>;
  /** Outstanding + loans per (book, snapshot, asset type); null type = not in tape. */
  asset_mix: Array<{ book_id: string; currency: string; date: string; asset_type: string | null; outstanding: number; loans: number }>;
};

export type AlertRecord = {
  id: string; type: string; severity: "CRITICAL" | "WARNING"; message: string;
  payload: Record<string, unknown> | null; acknowledged: boolean;
  acknowledged_at: string | null; created_at: string | null;
};

export type AlertsResponse = { alerts: AlertRecord[]; total: number };

export type BankAccountInfo = {
  id: string; deal_id: string; account_ref: string;
  currency: string | null; purpose: "collections" | "reserve" | "operating" | "transit";
  label: string | null; created_by: string | null; created_at: string | null;
  transactions_count?: number;
};

/**
 * Debita's asset class. Shared by the screening form and the book, so a
 * prospect's answer can seed the book it becomes.
 *
 * The canonical list lives in the backend (schemas/asset_class.py) and reaches
 * the UI through GET /asset-classes. This union exists for type-checking only —
 * render options from the endpoint, never from a literal array.
 */
export type AssetClass =
  | "CONSUMER"
  | "SME"
  | "FACTORING"
  | "AUTO"
  | "PAYROLL"
  | "BNPL"
  | "OTHER";

export type AssetClassOption = { value: AssetClass; label: string };

export type ApplicationStatus = "NEW" | "IN_REVIEW" | "APPROVED" | "REJECTED";

export type OriginatorApplication = {
  id: string;
  channel: "BD_REPRESENTATIVE" | "USDT_PROGRAM" | "ONLINE" | "OTHER";
  channel_detail: string | null;
  company_name: string;
  country: string;
  asset_type: "CONSUMER" | "SME" | "FACTORING" | "AUTO" | "PAYROLL" | "BNPL" | "OTHER";
  asset_type_detail: string | null;
  portfolio_balance: number | null;
  portfolio_currency: string;
  years_operating: number | null;
  monthly_origination: number | null;
  funding_amount: number | null;
  contact_email: string;
  loan_tape_name: string | null;
  loan_tape_size: number | null;
  payment_tape_name: string | null;
  payment_tape_size: number | null;
  documents: ApplicationDocument[];
  status: ApplicationStatus;
  created_at: string | null;
};

export type ApplicationDocument = {
  id: string;
  doc_type: string;
  label: string;
  slug: string; // kebab-case path segment (single-doc download endpoint)
  name: string | null;
  size: number | null;
};

export type FileRecord = {
  id: string; file_type: string; original_name: string | undefined;
  row_count: number | null; snapshot_date: string | null;
  processing_error: string | null;
  uploaded_by: string | null; uploaded_at: string | null;
  file_size: number | null; validation_warnings: string[] | null;
};

/** The TTL table, as a function. Exported so `lib/apiCache.test.ts` can assert
 *  it path by path: a new URL whose prefix is not listed here does not fail, it
 *  silently takes `TTL.default`, and a key nothing invalidates serves stale
 *  data forever. The order of the tests matters as much as their presence —
 *  the `includes` checks run first and would capture a `/deals/...` path that
 *  happened to contain one of their fragments. */
export function ttlForPath(path: string): number {
  if (path.includes("/analytics")) return TTL.analytics;
  if (path.includes("/metrics"))   return TTL.metrics;
  if (path.includes("/alerts"))    return TTL.alerts;
  if (path.includes("/files"))     return TTL.files;
  if (path.includes("/schema"))    return TTL.schema;
  if (path.startsWith("/facilities"))   return TTL.facilities;
  // A new URL prefix with no TTL entry does not fail: it silently takes
  // `default`, and a cache key that nothing invalidates serves stale data
  // forever. `/deals` is listed here and invalidated by every mutation below
  // for that reason.
  if (path.startsWith("/deals"))        return TTL.deals;
  return TTL.default;
}

async function cachedFetch<T = unknown>(path: string, opts?: { silent404?: boolean; silent403?: boolean }): Promise<T> {
  const hit = cacheGet(path);
  if (hit !== null) return hit as T;

  const existing = getInFlight(path);
  if (existing) return existing as Promise<T>;

  const promise = apiFetch(path, undefined, opts).then((data) => {
    cacheSet(path, data, ttlForPath(path));
    deleteInFlight(path);
    return data;
  }).catch((e) => {
    deleteInFlight(path);
    throw e;
  });

  setInFlight(path, promise);
  return promise;
}

export { cacheInvalidate };

export async function apiFetch(
  path: string,
  options?: RequestInit,
  // `silent403` is for reads the caller already knows may be denied — a surface
  // the reader's grant does not cover, fetched speculatively and rendered empty.
  // It suppresses the toast only; the ApiError still throws, so a caller that
  // does not handle it still fails loudly. Never set it on a write.
  opts?: { silent404?: boolean; silent403?: boolean },
) {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: { ...headers(), ...options?.headers },
    });
  } catch {
    const message = "Could not connect to server";
    _onApiError?.(message, 0);
    throw new ApiError(message, 0);
  }
  if (!res.ok) {
    if (res.status === 401) {
      clearToken();
      window.location.href = "/login";
      throw new ApiError("Session expired", 401);
    }
    const body = await res.json().catch(() => ({} as Record<string, unknown>));
    const message = _extractHttpError(body, `API error ${res.status}`);
    const isBorrowerIdentity = path.startsWith("/borrower/me");
    const isSilenced404 = res.status === 404 && opts?.silent404;
    const isSilenced403 = res.status === 403 && opts?.silent403;
    if (!(res.status === 403 && isBorrowerIdentity) && !isSilenced404 && !isSilenced403) {
      _onApiError?.(message, res.status);
    }
    throw new ApiError(message, res.status);
  }
  const ct = res.headers.get("content-type") ?? "";
  if (!ct.includes("application/json")) {
    const message = `Server returned non-JSON response (${ct || "unknown type"}) for ${path}`;
    _onApiError?.(message, res.status);
    throw new ApiError(message, res.status);
  }
  return res.json();
}

export async function apiUpload(
  path: string,
  file: File,
  extraParams?: Record<string, string>,
  /** Defaults to POST. PUT for single-slot resources that overwrite (borrower logo). */
  method: "POST" | "PUT" = "POST",
) {
  const form = new FormData();
  form.append("file", file);
  const params = extraParams ? "?" + new URLSearchParams(extraParams).toString() : "";
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}${params}`, {
      method,
      headers: headers(),
      body: form,
    });
  } catch {
    const message = "Could not connect to server";
    _onApiError?.(message, 0);
    throw new ApiError(message, 0);
  }
  if (!res.ok) {
    if (res.status === 401) {
      clearToken();
      window.location.href = "/login";
      throw new ApiError("Session expired", 401);
    }
    const body = await res.json().catch(() => ({} as Record<string, unknown>));
    const message = _extractHttpError(body, `Upload error ${res.status}`);
    _onApiError?.(message, res.status);
    throw new ApiError(message, res.status);
  }
  return res.json();
}

// Like apiUpload but returns the raw body on 409 instead of throwing,
// so the caller can handle group-conflict warnings.
export type UploadConflict = {
  conflict: true;
  blocked: boolean;
  reason: string;
  message: string;
  group_id: string;
  snapshot_date: string;
  file_count?: number;
};

export async function apiUploadAdmin(
  path: string,
  file: File,
  extraParams?: Record<string, string>,
): Promise<UploadResult | UploadConflict> {
  const form = new FormData();
  form.append("file", file);
  const params = extraParams ? "?" + new URLSearchParams(extraParams).toString() : "";
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}${params}`, {
      method: "POST",
      headers: headers(),
      body: form,
    });
  } catch {
    const message = "Could not connect to server";
    _onApiError?.(message, 0);
    throw new Error(message);
  }
  if (res.status === 409) {
    const body = await res.json().catch(() => ({} as Record<string, unknown>));
    const detail = (body.detail ?? body) as Record<string, unknown>;
    return {
      conflict: true,
      blocked: !!detail.blocked,
      reason: String(detail.reason ?? "group_ready"),
      message: String(detail.message ?? ""),
      group_id: String(detail.group_id ?? ""),
      snapshot_date: String(detail.snapshot_date ?? ""),
      file_count: typeof detail.file_count === "number" ? detail.file_count : undefined,
    } as UploadConflict;
  }
  if (res.status === 401) {
    clearToken();
    window.location.href = "/login";
    throw new Error("Session expired");
  }
  const body = await res.json().catch(() => ({} as Record<string, unknown>));
  return normalizeUploadResponse(res.status, body);
}


// Public originator screening form (/apply) — unauthenticated: no Authorization
// header, no login redirect, no global error toast. The page handles errors.
// XHR instead of fetch so large tape uploads can report progress.
export function apiSubmitApplication(
  form: FormData,
  onProgress?: (pct: number) => void,
): Promise<{ id: string; status: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_BASE}/apply`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      let body: Record<string, unknown> = {};
      try {
        body = JSON.parse(xhr.responseText) as Record<string, unknown>;
      } catch {
        // non-JSON body — handled by status check below
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(body as { id: string; status: string });
      } else {
        reject(new ApiError(_extractHttpError(body, `Submission error ${xhr.status}`), xhr.status));
      }
    };
    xhr.onerror = () => reject(new ApiError("Could not connect to server", 0));
    xhr.send(form);
  });
}

export const api = {
  // Auth
  generatePat: (durationDays: number): Promise<{ token: string; email: string; expires_at: string; duration_days: number }> =>
    apiFetch("/auth/pat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ duration_days: durationDays }),
    }),

  exchangeAuth0Token: async (accessToken: string): Promise<{ access_token: string; email: string }> => {
    let res: Response;
    try {
      res = await fetch(`${API_BASE}/auth/token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ access_token: accessToken }),
      });
    } catch {
      throw new Error("Could not connect to server.");
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const detail = (data as Record<string, unknown>)?.detail;
      if (detail && typeof detail === "object") {
        const d = detail as Record<string, unknown>;
        throw new ApiError(
          typeof d.message === "string" ? d.message : "Access denied.",
          res.status,
          typeof d.error === "string" ? d.error : undefined,
        );
      }
      throw new ApiError(typeof detail === "string" ? detail : "Access denied.", res.status);
    }
    return data;
  },

  // Borrower upload section
  borrowerInfo: () => apiFetch(`/borrower/me`),
  borrowerDeleteFile: (fileId: string) =>
    apiFetch(`/borrower/me/files/${fileId}`, { method: "DELETE" }),
  borrowerDownloadFile: async (fileId: string, fileName: string) => {
    const res = await fetch(`${API_BASE}/borrower/me/download/${fileId}`, { headers: headers() });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  },
  borrowerUploadFile: async (
    file: File,
    fileType: string,
    snapshotDate: string,
    signal?: AbortSignal,
  ): Promise<UploadResult> => {
    const form = new FormData();
    form.append("file", file);
    const params = `?file_type=${encodeURIComponent(fileType)}&snapshot_date=${encodeURIComponent(snapshotDate)}`;
    let res: Response;
    try {
      res = await fetch(`${API_BASE}/borrower/me/upload${params}`, {
        method: "POST",
        headers: headers(),
        body: form,
        signal,
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") throw err;
      throw new Error("Could not connect to server");
    }
    if (res.status === 401) {
      clearToken();
      window.location.href = "/login";
      throw new Error("Session expired");
    }
    const body = await res.json().catch(() => ({} as Record<string, unknown>));
    return normalizeUploadResponse(res.status, body);
  },

  // Health
  health: () => apiFetch("/health"),

  // Originator applications (public /apply screening form; admin review)
  listApplications: (): Promise<{ applications: OriginatorApplication[]; total: number; new_count: number }> =>
    apiFetch("/applications"),
  getApplication: (id: string): Promise<OriginatorApplication> => apiFetch(`/applications/${id}`),
  updateApplicationStatus: (id: string, status: ApplicationStatus): Promise<OriginatorApplication> =>
    apiFetch(`/applications/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    }),
  downloadApplicationFile: async (id: string, kind: string, filename: string) => {
    const res = await fetch(`${API_BASE}/applications/${id}/files/${kind}`, { headers: headers() });
    if (res.status === 401) { clearToken(); window.location.href = "/login"; throw new Error("Session expired"); }
    if (!res.ok) throw new Error("Download failed");
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(blobUrl);
  },
  downloadApplicationDocument: async (appId: string, documentId: string, filename: string) => {
    const res = await fetch(`${API_BASE}/applications/${appId}/documents/${documentId}`, { headers: headers() });
    if (res.status === 401) { clearToken(); window.location.href = "/login"; throw new Error("Session expired"); }
    if (!res.ok) throw new Error("Download failed");
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(blobUrl);
  },

  // Borrowers (HU-046)
  listBorrowers: () => apiFetch("/borrowers"),
  getBorrower: (id: string) => apiFetch(`/borrowers/${id}`),
  createBorrower: (body: {
    legal_name: string;
    currency: string;
    aliases?: string[] | null;
    country?: string | null;
    jurisdiction?: string | null;
    kyc_status?: "PENDING" | "APPROVED" | "REJECTED";
    kyc_metadata?: Record<string, unknown> | null;
  }) =>
    apiFetch("/borrowers", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
  updateBorrower: (id: string, body: Record<string, unknown>) =>
    apiFetch(`/borrowers/${id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
  deleteBorrower: (id: string, force = false) =>
    apiFetch(`/borrowers/${id}${force ? "?force=true" : ""}`, { method: "DELETE" }),

  /**
   * Every borrower's logo as a data URI, keyed by id. One cached call feeds
   * every table: the value goes straight into <img src>, no auth header needed.
   * Borrowers without a logo are absent from the map.
   */
  getBorrowerLogos: () => cachedFetch<{ logos: Record<string, string> }>(`/borrowers/logos`),
  /** PUT — a borrower has one logo and re-uploading overwrites it. */
  uploadBorrowerLogo: async (id: string, file: File) => {
    const res = await apiUpload(`/borrowers/${id}/logo`, file, undefined, "PUT");
    // Without this the tables keep the old mark for up to the cache TTL, which
    // reads as "the upload did nothing".
    cacheInvalidate("/borrowers/logos");
    return res;
  },
  deleteBorrowerLogo: async (id: string) => {
    const res = await apiFetch(`/borrowers/${id}/logo`, { method: "DELETE" });
    cacheInvalidate("/borrowers/logos");
    return res;
  },

  /**
   * The originator's presentation card (HU-064). Readable by an admin and by
   * anybody holding a permission on one of the borrower's books, which is what
   * lets the book's Profile tab and the facility's Originator block render it
   * without the admin-only borrower detail endpoint.
   */
  getBorrowerProfile: (id: string) =>
    cachedFetch<BorrowerProfileResponse>(`/borrowers/${id}/profile`),
  /** PUT — the editor holds the whole document and replaces it. Admin only. */
  updateBorrowerProfile: async (id: string, body: BorrowerProfileInput) => {
    const res = await apiFetch(`/borrowers/${id}/profile`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    // Otherwise the read surfaces keep the previous card for up to the cache
    // TTL, which reads as "the save did nothing".
    cacheInvalidate(`/borrowers/${id}/profile`);
    return res as BorrowerProfileResponse;
  },

  /**
   * The originator's own accounts (HU-065). Same read rule as the profile:
   * admin, or anybody holding a permission on one of the borrower's books —
   * the extracted figures are magnitudes of the entity.
   *
   * Ratios and coherence checks arrive computed; neither is stored, so nothing
   * here should ever be recomputed in the client.
   */
  getBorrowerFinancials: (id: string) =>
    cachedFetch<BorrowerFinancialsResponse>(`/borrowers/${id}/financials`),
  deleteBorrowerFinancialsPeriod: async (id: string, periodId: string) => {
    const res = await apiFetch(`/borrowers/${id}/financials/${periodId}`, { method: "DELETE" });
    cacheInvalidate(`/borrowers/${id}/financials`);
    return res;
  },

  /** The statements as delivered. Admin only: a set of accounts carries
   *  letterhead, company number and the auditor's signature. */
  getFinancialDocuments: (id: string) =>
    cachedFetch<{ documents: FinancialDocumentRecord[] }>(`/borrowers/${id}/financial-documents`),
  uploadFinancialDocument: async (
    id: string,
    file: File,
    docType: string,
    periodLabel?: string,
  ) => {
    const res = await apiUpload(`/borrowers/${id}/financial-documents`, file, {
      doc_type: docType,
      ...(periodLabel ? { period_label: periodLabel } : {}),
    });
    cacheInvalidate(`/borrowers/${id}/financial-documents`);
    return res as FinancialDocumentRecord;
  },
  downloadFinancialDocument: async (id: string, documentId: string, filename: string) => {
    const res = await fetch(`${API_BASE}/borrowers/${id}/financial-documents/${documentId}/download`, {
      headers: headers(),
    });
    if (res.status === 401) { clearToken(); window.location.href = "/login"; throw new Error("Session expired"); }
    if (!res.ok) throw new Error("Download failed");
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(blobUrl);
  },

  // Books (HU-046)
  /** Debita's asset-class vocabulary. Static, so cached — never hardcode the list here. */
  getAssetClasses: () =>
    cachedFetch<{ asset_classes: AssetClassOption[] }>(`/asset-classes`),
  listBorrowerBooks: (borrowerId: string) => apiFetch(`/borrowers/${borrowerId}/books`),
  getBook: (id: string) => apiFetch(`/books/${id}`),
  // Analyst notes — Debita's margin notes on a book, keyed to the tab they
  // were written on. Admin-only on the server.
  getBookNotes: (id: string) => apiFetch(`/books/${id}/notes`),
  addBookNote: (id: string, section: string, body: string, visibility: "internal" | "external" = "internal") =>
    apiFetch(`/books/${id}/notes`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ section, body, visibility }),
    }),
  deleteBookNote: (id: string, noteId: string) =>
    apiFetch(`/books/${id}/notes/${noteId}`, { method: "DELETE" }),
  getBookAnalytics: (id: string, snapshotDate?: string) =>
    apiFetch(`/books/${id}/analytics${snapshotDate ? `?snapshot_date=${snapshotDate}` : ""}`),
  getBookProposedStructure: (id: string) =>
    apiFetch(`/books/${id}/proposed-structure`),
  stressSimulate: (facilityId: string, body: Record<string, unknown>) =>
    apiFetch(`/facilities/${facilityId}/stress-simulate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  getBookSnapshotDates: (id: string) =>
    cachedFetch<string[]>(`/books/${id}/analytics/available-dates`),
  getBookMetricsHistory: (id: string) =>
    cachedFetch<Record<string, unknown>[]>(`/books/${id}/metrics/history`),
  getBookLoans: (
    id: string,
    params: {
      dpd_min?: number;
      dpd_max?: number;
      lifecycle_status?: string;
      exclude_status?: string;
      order_by?: string;
      limit?: number;
      offset?: number;
      snapshot_date?: string;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null) qs.append(k, String(v));
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return cachedFetch<{
      book_id: string;
      snapshot_date: string | null;
      total: number;
      limit?: number;
      offset?: number;
      loans: Array<{
        loan_id: string;
        external_loan_id: string;
        debtor_id: string | null;
        debtor_name: string | null;
        outstanding: number;
        original_amount: number | null;
        /** null = the tape reported no DPD for this loan (not 0). */
        dpd: number | null;
        bucket: string;
        vintage_month: string | null;
        lifecycle_status: string;
      }>;
    }>(`/books/${id}/loans${suffix}`);
  },
  createBook: (
    borrowerId: string,
    body: {
      currency: string;
      tape_mapping_config: Record<string, unknown>;
      payment_mapping_config: Record<string, unknown>;
      customer_mapping_config?: Record<string, unknown> | null;
      collateral_mapping_config?: Record<string, unknown> | null;
      schedule_mapping_config?: Record<string, unknown> | null;
      bank_config?: Record<string, unknown> | null;
      asset_class?: string | null;
    },
  ) =>
    apiFetch(`/borrowers/${borrowerId}/books`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  updateBook: (id: string, body: Record<string, unknown>) =>
    apiFetch(`/books/${id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
  deleteBook: (id: string, force = false) =>
    apiFetch(`/books/${id}${force ? "?force=true" : ""}`, { method: "DELETE" }),
  runBookIngest: (id: string, body: { snapshot_date?: string | null; cascade_to_facilities?: boolean }) =>
    apiFetch(`/books/${id}/run-ingest`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),

  // Deals — the contract. Every writer below drops BOTH prefixes: a Deal
  // mutation moves facilities (the alta creates them, the delete takes them
  // down) and a facility mutation moves its Deal (the list, the count and the
  // derived status). `cacheInvalidate` clears by prefix, so "/deals" also
  // clears "/deals/{id}" and "/deals/{id}/access".
  listDeals: (bookId?: string) =>
    cachedFetch<DealListItem[]>(`/deals${bookId ? `?book_id=${bookId}` : ""}`),
  getDeal: (id: string) => cachedFetch<DealInfo>(`/deals/${id}`),
  createDeal: (body: {
    deal_reference: string;
    deal_name: string;
    book_id: string;
    currency: string;
    deal_config?: Record<string, unknown>;
    facilities: NewDealFacility[];
  }) =>
    apiFetch("/deals", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
      .then((r) => { cacheInvalidate("/deals"); cacheInvalidate("/facilities"); return r; }),
  updateDeal: (id: string, body: { deal_name?: string; deal_reference?: string; currency?: string }) =>
    apiFetch(`/deals/${id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
      .then((r) => { cacheInvalidate("/deals"); return r; }),
  updateDealConfig: (id: string, body: Record<string, unknown>) =>
    apiFetch(`/deals/${id}/config`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
      .then((r) => { cacheInvalidate("/deals"); return r as { status: string; deal_config: DealConfig; deal_config_effective: DealConfig }; }),
  // Admin-only. Under `/deals`, so the config PUT above drops it with the rest:
  // which annex is declared is part of the answer.
  getDealConfigOptions: (id: string) => cachedFetch<DealConfigOptions>(`/deals/${id}/config-options`),
  // The two read-models of the contract (A4, A5). Cached like every other
  // `/deals` read — the prefix rule in `ttlForPath` reaches them, and so does
  // `cacheInvalidate("/deals")`, which clears by prefix. What no front mutator
  // touches is what feeds them (`covenant_observations`, `daily_metrics`): the
  // pipeline writes those, so the run that finishes drops the prefix too
  // (PipelineProgressModal), and 30 s of staleness is only ever 30 s of a
  // contract that did not move.
  getDealCovenants: (id: string, asOf?: string) =>
    cachedFetch<DealCovenants>(`/deals/${id}/covenants${asOf ? `?as_of=${asOf}` : ""}`),
  getDealAggregate: (id: string, asOf?: string) =>
    cachedFetch<DealAggregate>(`/deals/${id}/aggregate${asOf ? `?as_of=${asOf}` : ""}`),
  // The third (C2), same prefix and same reasoning: what feeds it —
  // `bank_transactions` and the confirmed matches — is written by the statement
  // upload and by the analyst's queue, both of which already drop `/deals`.
  getDealCash: (id: string, asOf?: string) =>
    cachedFetch<DealCash>(`/deals/${id}/cash${asOf ? `?as_of=${asOf}` : ""}`),
  // Same TTL and same invalidation as the cash: it is the cash, converted, plus
  // the waterfall runs, which only the pipeline writes.
  getDealDistributions: (id: string, asOf?: string) =>
    cachedFetch<DealDistributions>(`/deals/${id}/distributions${asOf ? `?as_of=${asOf}` : ""}`),

  // The contract's Payment Report: the §8 ladder with what each rung has
  // accrued and what it will have cost by the payment date. Computed on read
  // from the facilities' period cost, so it moves when a movement is recorded
  // and not only when a run happens.
  getDealPeriodCost: (id: string, asOf?: string) =>
    cachedFetch<DealPeriodLadder>(`/deals/${id}/period-cost${asOf ? `?as_of=${asOf}` : ""}`),

  getDealAudit: (id: string) => apiFetch(`/deals/${id}/audit`) as Promise<DealAuditEntry[]>,
  // Read fresh, never cached: it is shown to answer "who gains access if I add
  // this facility", and a stale answer to that question is the wrong answer.
  getDealAccess: (id: string) => apiFetch(`/deals/${id}/access`) as Promise<DealAccess>,
  deleteDeal: (id: string, force = false) =>
    apiFetch(`/deals/${id}${force ? "?force=true" : ""}`, { method: "DELETE" })
      .then((r) => { cacheInvalidate("/deals"); cacheInvalidate("/facilities"); return r; }),

  // Facilities
  listFacilities: () => cachedFetch<FacilityListItem[]>("/facilities"),
  facilitiesSummary: () => cachedFetch<FacilitySummaryEntry[]>("/facilities/summary"),
  overviewSeries: () => cachedFetch<OverviewSeriesResponse>("/facilities/overview-series"),
  overviewAnalytics: () => cachedFetch<OverviewAnalyticsResponse>("/facilities/overview-analytics"),
  hydrateFacility: (id: string) => cachedFetch<{ facility: FacilityInfo; metrics: Metrics | null; history: Metrics[]; analytics: Analytics | null }>(`/facilities/${id}/hydrate`),
  // `deal_id`, not `borrower_id`: the contract says which book the collateral
  // comes from and who the borrower is, so both are read off it (D3).
  createFacility: (body: NewDealFacility & { deal_id: string }) =>
    // Drop the cached facility lists here rather than at the call site: a new facility
    // that does not show up until the tab is reloaded is the failure mode, and
    // the writer is the only place guaranteed to know it happened. Callers that
    // render from a store must still refresh it (see useGlobalStore.refreshFacilities).
    apiFetch("/facilities", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
      .then((r) => { cacheInvalidate("/facilities"); cacheInvalidate("/deals"); return r; }),
  getFacility: (id: string) => cachedFetch<FacilityInfo>(`/facilities/${id}`),
  activateFacility: (id: string) =>
    apiFetch(`/facilities/${id}/activate`, { method: "POST" })
      .then((r) => { cacheInvalidate("/facilities"); cacheInvalidate("/deals"); return r; }),

  // Schema
  loanTapeSchema: () => cachedFetch("/facilities/schema/loan-tape"),
  bankSchema: () => cachedFetch("/facilities/schema/bank-statement"),
  paymentSchema: () => cachedFetch("/facilities/schema/payment-tape"),
  scheduleSchema: () => cachedFetch("/facilities/schema/schedule"),
  customerSchema: () => cachedFetch("/facilities/schema/customer"),
  collateralSchema: () => cachedFetch("/facilities/schema/collateral"),

  // Sample analysis + AI mapping suggestion. Read-only helpers; they analyse an
  // uploaded sample and propose a column_map, they never persist anything.
  analyzeSample: (id: string, file: File) => apiUpload(`/facilities/${id}/analyze-sample`, file),
  suggestMapping: (id: string) => apiFetch(`/facilities/${id}/suggest-mapping`, { method: "POST" }),

  // Mapping is book-scoped (HU-046): read it with getBook, write it with
  // updateBook. There is deliberately no facility-scoped mapping writer — the old
  // `updateMapping` let a single facility's form replace the whole book's config.

  // Eligibility and covenants. There is deliberately no cascade writer: the
  // form that used to be here wrote the pre-catalogue step shape — a free-text
  // category and one of six `calculation_type` words — which the schema now
  // migrates on read and cannot round-trip, so saving emptied the cascade. The
  // order of payment is the CONTRACT's anyway, declared on the Deal.
  updateEligibility: (id: string, body: Record<string, unknown>) =>
    apiFetch(`/facilities/${id}/eligibility`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
  updateCovenants: (id: string, body: Record<string, unknown>) =>
    apiFetch(`/facilities/${id}/covenants`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
  // Persisted waterfall runs + instructions (ROADMAP #37)
  getWaterfallRuns: (id: string, params?: { from?: string; to?: string; status?: string; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.from) q.set("from", params.from);
    if (params?.to) q.set("to", params.to);
    if (params?.status) q.set("status", params.status);
    if (params?.limit) q.set("limit", String(params.limit));
    const qs = q.toString() ? `?${q}` : "";
    return cachedFetch<WaterfallRunsResponse>(`/facilities/${id}/waterfall/runs${qs}`);
  },
  getWaterfallRunDetail: (id: string, runId: string) =>
    cachedFetch<WaterfallRunRecord>(`/facilities/${id}/waterfall/runs/${runId}`),
  // Covenant observation series (HU-059.4) — admin-only endpoint
  covenantObservations: (id: string, params?: { covenant_key?: string; from?: string; to?: string }) => {
    const q = new URLSearchParams();
    if (params?.covenant_key) q.set("covenant_key", params.covenant_key);
    if (params?.from) q.set("from", params.from);
    if (params?.to) q.set("to", params.to);
    const qs = q.toString() ? `?${q}` : "";
    return cachedFetch<CovenantObservationsResponse>(`/facilities/${id}/covenants/observations${qs}`);
  },
  // Acquisition-vintage cohorts (HU-062.4) — collections-to-date % of funded
  // by pool-entry vintage, derived from the pledge history.
  facilityAcquisitionCohorts: (id: string) =>
    cachedFetch<AcquisitionCohortsResponse>(`/facilities/${id}/acquisition-cohorts`),
  // Facility "At a Glance" + exposure (HU-062.5 window B) — Percent parity.
  facilityGlance: (id: string) =>
    cachedFetch<FacilityGlanceResponse>(`/facilities/${id}/facility-glance`),
  // Facility line drawn (HU-063) — ops surface. Uncached (mutates on write).
  facilityLineMovements: (id: string): Promise<LineMovementsResponse> =>
    apiFetch(`/facilities/${id}/line-movements`),
  facilityAddLineMovement: (id: string, body: { effective_date: string; kind: "DRAW" | "REPAYMENT"; amount: number; note?: string | null }) =>
    apiFetch(`/facilities/${id}/line-movements`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
  facilityDeleteLineMovement: (id: string, movementId: string) =>
    apiFetch(`/facilities/${id}/line-movements/${movementId}`, { method: "DELETE" }),
  // Securitization notes (HU-063 fase 2) — ops surface. Uncached (mutates).
  facilityNotes: (id: string): Promise<NoteLedger> =>
    apiFetch(`/facilities/${id}/notes`),
  /** There is no «create tranche»: a series IS a facility, so its face value,
   *  its coupon and its two dates are declared where every other term of a
   *  facility is. What is left is the repayment ledger. */
  facilityAddAmortisation: (id: string, body: { amort_date: string; principal_amortised: number; interest_paid?: number | null }) =>
    apiFetch(`/facilities/${id}/notes/amortisation`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
  facilityDeleteAmortisation: (id: string, amortId: string) =>
    apiFetch(`/facilities/${id}/notes/amortisation/${amortId}`, { method: "DELETE" }),
  // DPD × dimension cross-tabs (HU-062.5 window C) — Percent parity.
  facilityDpdStratification: (id: string) =>
    cachedFetch<DpdStratificationResponse>(`/facilities/${id}/dpd-stratification`),
  // Asset test — unified collateral-coverage cascade (HU-062.5 window D).
  // `asOf` reads the cascade of the last finished measurement on or before it.
  facilityAssetTest: (id: string, asOf?: string) =>
    cachedFetch<AssetTestResponse>(`/facilities/${id}/asset-test${asOf ? `?as_of=${asOf}` : ""}`),
  // What the current period costs, with the arithmetic behind each figure.
  facilityPeriodCost: (id: string, asOf?: string) =>
    cachedFetch<PeriodCostResponse>(
      `/facilities/${id}/period-cost${asOf ? `?as_of=${asOf}` : ""}`,
    ),
  // Incidents (HU-059.3) — admin-only endpoints
  getFacilityIncidents: (id: string, params?: { incident_type?: string; status?: string; severity?: string; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.incident_type) q.set("incident_type", params.incident_type);
    if (params?.status) q.set("status", params.status);
    if (params?.severity) q.set("severity", params.severity);
    if (params?.limit) q.set("limit", String(params.limit));
    const qs = q.toString() ? `?${q}` : "";
    return cachedFetch<{ facility_id: string; total: number; incidents: IncidentRecord[] }>(`/facilities/${id}/incidents${qs}`);
  },
  // Eligibility views: substitution reserve / ceded-not-eligible (HU-059.2).
  // 404 = no eligibility evaluation yet — the block renders a placeholder.
  getEligibilityViews: (id: string, snapshotDate?: string) => {
    const qs = snapshotDate ? `?snapshot_date=${snapshotDate}` : "";
    return cachedFetch<EligibilityViews>(`/facilities/${id}/eligibility/views${qs}`, { silent404: true });
  },
  updateFacilityConfig: (id: string, body: Record<string, unknown>) =>
    apiFetch(`/facilities/${id}/facility-config`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
  // HU-059.7 — manual re-run after editing config (never automatic on save)
  recalculateFacility: (id: string) =>
    apiFetch(`/facilities/${id}/recalculate`, { method: "POST" }) as Promise<{
      status: string;
      facility_id: string;
      mode: "determination" | "snapshot" | "draft";
    }>,
  getFacilityAudit: (id: string) => apiFetch(`/facilities/${id}/audit`),

  // Analyst notes (variable-level commentary; surfaced as pulse dots in the UI).
  // Debita's own commentary on the counterparty — admin only, both here and at
  // the endpoint, so no non-admin caller exists to be refused.
  getAnalystNotes: (id: string) =>
    apiFetch(`/facilities/${id}/analyst-notes`) as Promise<{
      facility_id: string;
      notes: Record<string, { note: string; author: string; ts: string; scope?: string | null; read_by?: string[] }>;
    }>,
  updateAnalystNotes: (
    id: string,
    notes: Record<string, { note: string; scope?: string | null } | null>,
  ) =>
    apiFetch(`/facilities/${id}/analyst-notes`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ notes }),
    }),
  markAnalystNoteRead: (id: string, variable_key: string) =>
    apiFetch(`/facilities/${id}/analyst-notes/read`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ variable_key }),
    }),

  // Metrics & Analytics
  facilityStatus: (id: string) => apiFetch(`/facilities/${id}/status`),
  facilityMetrics: (id: string) => cachedFetch<Metrics>(`/facilities/${id}/metrics/latest`),
  facilityMetricsHistory: (id: string) => cachedFetch<Metrics[]>(`/facilities/${id}/metrics/history`),
  getFacilityAnalytics: (id: string, snapshotDate?: string, eligibleOnly?: boolean) => {
    const params = new URLSearchParams();
    if (snapshotDate) params.set("snapshot_date", snapshotDate);
    if (eligibleOnly) params.set("eligible_only", "true");
    const qs = params.toString() ? `?${params}` : "";
    // eligible-scope 404s are expected for dates never covered by a
    // post-HU-059.2 facility run (forward-only) — no global error toast.
    return cachedFetch<Analytics>(`/facilities/${id}/analytics${qs}`, eligibleOnly ? { silent404: true } : undefined);
  },
  getFacilitySnapshotDates: (id: string) =>
    cachedFetch<string[]>(`/facilities/${id}/analytics/available-dates`),

  // Alerts
  getAlerts: (id: string, params?: { severity?: string; acknowledged?: boolean; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.severity) q.set("severity", params.severity);
    if (params?.acknowledged !== undefined) q.set("acknowledged", String(params.acknowledged));
    if (params?.limit) q.set("limit", String(params.limit));
    const qs = q.toString() ? `?${q}` : "";
    return cachedFetch<AlertsResponse>(`/facilities/${id}/alerts${qs}`);
  },
  getGlobalAlerts: (params?: { severity?: string; acknowledged?: boolean; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.severity) q.set("severity", params.severity);
    if (params?.acknowledged !== undefined) q.set("acknowledged", String(params.acknowledged));
    if (params?.limit) q.set("limit", String(params.limit));
    const qs = q.toString() ? `?${q}` : "";
    return cachedFetch<(AlertRecord & { facility_name: string })[]>(`/alerts${qs}`);
  },
  acknowledgeAlert: (facilityId: string, alertId: string) =>
    apiFetch(`/facilities/${facilityId}/alerts/${alertId}/acknowledge`, { method: "POST" }),

  // Incidents (HU-060.1) — global inbox + facility tab share the global list
  // (priority-ordered server-side; facility tab passes facility_id).
  getGlobalIncidents: (params?: {
    status?: string; incident_type?: string; severity?: string;
    facility_id?: string; limit?: number; offset?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set("status", params.status);
    if (params?.incident_type) q.set("incident_type", params.incident_type);
    if (params?.severity) q.set("severity", params.severity);
    if (params?.facility_id) q.set("facility_id", params.facility_id);
    if (params?.limit) q.set("limit", String(params.limit));
    if (params?.offset) q.set("offset", String(params.offset));
    const qs = q.toString() ? `?${q}` : "";
    return cachedFetch<IncidentRecord[]>(`/incidents${qs}`);
  },
  getIncidentsSummary: (params?: { facility_id?: string }) => {
    const qs = params?.facility_id ? `?facility_id=${params.facility_id}` : "";
    return cachedFetch<IncidentsSummary>(`/incidents/summary${qs}`);
  },
  getIncidentDetail: (facilityId: string, incidentId: string) =>
    apiFetch(`/facilities/${facilityId}/incidents/${incidentId}`) as Promise<IncidentDetail>,
  transitionIncident: (facilityId: string, incidentId: string, body: { status: IncidentStatus; note?: string }) =>
    apiFetch(`/facilities/${facilityId}/incidents/${incidentId}/transition`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }) as Promise<IncidentRecord>,

  // Files & Pipeline Runs — book-level (canonical since HU-046)
  listFilesBook: (bookId: string, fileType?: string) => {
    const q = fileType ? `?file_type=${fileType}` : "";
    return cachedFetch<FileRecord[]>(`/books/${bookId}/files${q}`);
  },
  listUploadGroupsBook: (bookId: string) => apiFetch(`/books/${bookId}/upload-groups`),
  listPipelineRunsBook: (bookId: string, limit = 10, offset = 0) =>
    apiFetch(`/books/${bookId}/pipeline-runs?limit=${limit}&offset=${offset}`),
  getPipelineRunDetailBook: (bookId: string, runId: string) =>
    apiFetch(`/books/${bookId}/pipeline-runs/${runId}/detail`),
  getPipelineRunDetailUnified: (scopeId: string, scopeType: "book" | "facility", runId: string) =>
    scopeType === "facility"
      ? apiFetch(`/facilities/${scopeId}/pipeline-runs/${runId}/detail`)
      : apiFetch(`/books/${scopeId}/pipeline-runs/${runId}/detail`),
  deleteFileBook: (bookId: string, fileId: string) =>
    apiFetch(`/books/${bookId}/files/${fileId}`, { method: "DELETE" }),
  downloadFileBook: async (bookId: string, fileId: string, filename?: string) => {
    const { url: presignedUrl, filename: serverFilename } = await apiFetch(
      `/books/${bookId}/files/${fileId}/presigned-url`
    );
    if (presignedUrl) {
      const a = document.createElement("a");
      a.href = presignedUrl;
      a.download = filename || serverFilename || "download";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }
    const res = await fetch(`${API_BASE}/books/${bookId}/files/${fileId}/download`, {
      headers: headers(),
    });
    if (res.status === 401) { clearToken(); window.location.href = "/login"; throw new Error("Session expired"); }
    if (!res.ok) throw new Error("Download failed");
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename || "download";
    a.click();
    URL.revokeObjectURL(blobUrl);
  },
  deleteUploadGroupBook: (bookId: string, groupId: string) =>
    apiFetch(`/books/${bookId}/upload-groups/${groupId}`, { method: "DELETE" }),
  deleteRunBook: (bookId: string, runId: string) =>
    apiFetch(`/books/${bookId}/pipeline-runs/${runId}`, { method: "DELETE" }),
  runPipelineBook: (bookId: string, opts: {
    loanTapeFileId?: string; paymentTapeFileId?: string;
    scheduleFileId?: string; customerFileId?: string; collateralFileId?: string;
    force?: boolean;
  } = {}) => {
    const params = new URLSearchParams();
    if (opts.loanTapeFileId) params.set("loan_tape_file_id", opts.loanTapeFileId);
    if (opts.paymentTapeFileId) params.set("payment_tape_file_id", opts.paymentTapeFileId);
    if (opts.scheduleFileId) params.set("schedule_file_id", opts.scheduleFileId);
    if (opts.customerFileId) params.set("customer_file_id", opts.customerFileId);
    if (opts.collateralFileId) params.set("collateral_file_id", opts.collateralFileId);
    if (opts.force) params.set("force", "true");
    return apiFetch(`/books/${bookId}/run-pipeline?${params}`, { method: "POST" });
  },
  uploadLoanTapeBook: (bookId: string, file: File, snapshotDate?: string, replace?: boolean) =>
    apiUploadAdmin(`/books/${bookId}/upload/loan-tape`, file, {
      ...(snapshotDate ? { snapshot_date: snapshotDate } : {}),
      ...(replace ? { replace: "true" } : {}),
    }),
  uploadPaymentTapeBook: (bookId: string, file: File, snapshotDate?: string, replace?: boolean) =>
    apiUploadAdmin(`/books/${bookId}/upload/payment-tape`, file, {
      ...(snapshotDate ? { snapshot_date: snapshotDate } : {}),
      ...(replace ? { replace: "true" } : {}),
    }),
  uploadScheduleBook: (bookId: string, file: File, snapshotDate?: string, replace?: boolean) =>
    apiUploadAdmin(`/books/${bookId}/upload/schedule`, file, {
      ...(snapshotDate ? { snapshot_date: snapshotDate } : {}),
      ...(replace ? { replace: "true" } : {}),
    }),
  uploadCustomerBook: (bookId: string, file: File, snapshotDate?: string, replace?: boolean) =>
    apiUploadAdmin(`/books/${bookId}/upload/customer`, file, {
      ...(snapshotDate ? { snapshot_date: snapshotDate } : {}),
      ...(replace ? { replace: "true" } : {}),
    }),
  uploadCollateralBook: (bookId: string, file: File, snapshotDate?: string, replace?: boolean) =>
    apiUploadAdmin(`/books/${bookId}/upload/collateral`, file, {
      ...(snapshotDate ? { snapshot_date: snapshotDate } : {}),
      ...(replace ? { replace: "true" } : {}),
    }),

  // Files & Pipeline Runs — facility-level (@deprecated, use book-level equivalents above)
  listFiles: (id: string, fileType?: string) => {
    const q = fileType ? `?file_type=${fileType}` : "";
    return cachedFetch<FileRecord[]>(`/facilities/${id}/files${q}`);
  },
  // Same non-anonymous requirement as the files endpoints: the Overview reads
  // it only to date the last completed run, so a denial is rendered as "no run".
  listPipelineRuns: (id: string, limit = 10, offset = 0) =>
    apiFetch(`/facilities/${id}/pipeline-runs?limit=${limit}&offset=${offset}`, undefined, { silent403: true }),
  getPipelineRunDetail: (facilityId: string, runId: string) => apiFetch(`/facilities/${facilityId}/pipeline-runs/${runId}/detail`),
  downloadUnmatched: (facilityId: string, runId: string) => {
    const url = `${API_BASE}/facilities/${facilityId}/pipeline-runs/${runId}/unmatched.csv`;
    return fetch(url, { headers: headers() })
      .then(r => { if (r.status === 401) { clearToken(); window.location.href = "/login"; throw new Error("Session expired"); } if (!r.ok) throw new Error("No unmatched data"); return r.blob(); })
      .then(blob => {
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objUrl;
        a.download = `unmatched_${runId.slice(0, 8)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
      });
  },

  // Update facility — every mutation drops the cached facility AND deal lists
  // (see createFacility): the Deal payload carries its facilities, their count
  // and the status derived from them.
  updateFacility: (id: string, body: Record<string, unknown>) =>
    apiFetch(`/facilities/${id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
      .then((r) => { cacheInvalidate("/facilities"); cacheInvalidate("/deals"); return r; }),
  // A FULL replace of `pricing_config`: what is not sent is dropped, so the
  // caller sends back the stored blob with only its change. The Deal's Payment
  // Report prices the line from it, hence the second prefix.
  updateFacilityPricing: (id: string, body: Record<string, unknown>) =>
    apiFetch(`/facilities/${id}/pricing`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
      .then((r) => {
        cacheInvalidate("/facilities"); cacheInvalidate("/deals");
        return r as { status: string; pricing_config: Record<string, unknown> };
      }),
  // 409 when this is the last live facility of its Deal: a Deal is never
  // empty, so what is being asked for is `deleteDeal`.
  deleteFacility: (id: string, force = false) =>
    apiFetch(`/facilities/${id}${force ? "?force=true" : ""}`, { method: "DELETE" })
      .then((r) => { cacheInvalidate("/facilities"); cacheInvalidate("/deals"); return r; }),
  archiveFacility: (id: string) =>
    apiFetch(`/facilities/${id}`, { method: "DELETE" })
      .then((r) => { cacheInvalidate("/facilities"); cacheInvalidate("/deals"); return r; }),

  // Export
  exportMetricsUrl: (id: string) => `${API_BASE}/facilities/${id}/export/metrics`,
  exportLoanTapeUrl: (id: string, snapshotDate?: string) =>
    `${API_BASE}/facilities/${id}/export/loan-tape${snapshotDate ? `?snapshot_date=${snapshotDate}` : ""}`,

  // AI analysis
  analyze: (facilityId: string, message: string, pipelineResult?: Record<string, unknown>) =>
    apiFetch(`/facilities/${facilityId}/analyze`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message, pipeline_result: pipelineResult }),
    }),

  deleteFile: (facilityId: string, fileId: string) =>
    apiFetch(`/facilities/${facilityId}/files/${fileId}`, { method: "DELETE" }),

  // File download
  downloadFile: async (facilityId: string, fileId: string, filename?: string) => {
    // Try presigned URL first (S3/prod): browser downloads natively without buffering
    const { url: presignedUrl, filename: serverFilename } = await apiFetch(
      `/facilities/${facilityId}/files/${fileId}/presigned-url`
    );
    if (presignedUrl) {
      const a = document.createElement("a");
      a.href = presignedUrl;
      a.download = filename || serverFilename || "download";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }
    // Fallback for local storage (dev): proxy through API
    const res = await fetch(`${API_BASE}/facilities/${facilityId}/files/${fileId}/download`, {
      headers: headers(),
    });
    if (res.status === 401) { clearToken(); window.location.href = "/login"; throw new Error("Session expired"); }
    if (!res.ok) throw new Error("Download failed");
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename || "download";
    a.click();
    URL.revokeObjectURL(blobUrl);
  },

  // Audit
  getAuditTrail: (id: string) => apiFetch(`/facilities/${id}/audit`),

  // Portfolio
  runPortfolio: (id: string, loanTapePath?: string) => {
    const params = new URLSearchParams();
    if (loanTapePath) params.set("loan_tape_path", loanTapePath);
    return apiFetch(`/facilities/${id}/run-portfolio?${params}`, { method: "POST" });
  },

  // Upload groups (@deprecated — use listUploadGroupsBook)
  listUploadGroups: (id: string) => apiFetch(`/facilities/${id}/upload-groups`),

  // File upload & pipeline — facility-level (@deprecated for all except uploadBankStatement)
  uploadLoanTape: (id: string, file: File, snapshotDate?: string, replace?: boolean) =>
    apiUploadAdmin(`/facilities/${id}/upload/loan-tape`, file, {
      ...(snapshotDate ? { snapshot_date: snapshotDate } : {}),
      ...(replace ? { replace: "true" } : {}),
    }),
  uploadBankStatement: (id: string, file: File, sourceType?: string, snapshotDate?: string, accountId?: string) =>
    apiUpload(`/facilities/${id}/upload/bank-statement`, file, {
      ...(sourceType ? { source_type: sourceType } : {}),
      ...(snapshotDate ? { snapshot_date: snapshotDate } : {}),
      ...(accountId ? { account_id: accountId } : {}),
    }),

  // Bank accounts — entities of the contract: a Deal settles through them and
  // its facilities draw from them.
  listBankAccounts: (dealId: string) =>
    apiFetch(`/deals/${dealId}/bank-accounts`) as Promise<{ deal_id: string; total: number; accounts: BankAccountInfo[] }>,
  // A new account moves the cockpit's cash header, which is served under
  // `/facilities`, while the account itself lives under `/deals`. Both prefixes
  // go, like every other mutator that touches either side of the contract.
  createBankAccount: (dealId: string, body: { account_ref: string; purpose: string; currency?: string; label?: string }) =>
    apiFetch(`/deals/${dealId}/bank-accounts`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => { cacheInvalidate("/deals"); cacheInvalidate("/facilities"); return r; }),

  // Reconciliation — analyst queue (HU-059.5, operated from the UI in HU-060.2)
  getReconciliation: (facilityId: string) =>
    apiFetch(`/facilities/${facilityId}/reconciliation`) as Promise<ReconSummaryResponse>,
  getReconciliationQueue: (facilityId: string) =>
    apiFetch(`/facilities/${facilityId}/reconciliation/queue`) as Promise<ReconQueueResponse>,
  // Reconciliation ledger — the book (Track 1). docs/reconciliacion.md
  getReconciliationLedger: (facilityId: string) =>
    apiFetch(`/facilities/${facilityId}/reconciliation/ledger`) as Promise<LedgerResponse>,
  buildReconciliationLedger: (facilityId: string, asOf?: string) =>
    apiFetch(
      `/facilities/${facilityId}/reconciliation/ledger/build${asOf ? `?as_of=${asOf}` : ""}`,
      { method: "POST" },
    ) as Promise<LedgerResponse>,
  getReconciliationOpenItems: (facilityId: string, opts?: { overdueOnly?: boolean; limit?: number }) => {
    const q = new URLSearchParams();
    if (opts?.overdueOnly) q.set("overdue_only", "true");
    if (opts?.limit) q.set("limit", String(opts.limit));
    const qs = q.toString() ? `?${q}` : "";
    return apiFetch(`/facilities/${facilityId}/reconciliation/ledger/open-items${qs}`) as Promise<LedgerOpenItemsResponse>;
  },
  // Statement file history of the facility (the archive every case draws from).
  listFacilityFiles: (facilityId: string, fileType?: string) =>
    apiFetch(`/facilities/${facilityId}/files${fileType ? `?file_type=${fileType}` : ""}`) as Promise<FileRecord[]>,
  // Monthly reconciliation report: month-end close (frozen once issued).
  getMonthlyReconciliationReport: (facilityId: string, month: string) =>
    apiFetch(`/facilities/${facilityId}/reconciliation/ledger/monthly/${month}`) as Promise<MonthlyReport>,
  issueMonthlyReconciliationReport: (facilityId: string, month: string) =>
    apiFetch(`/facilities/${facilityId}/reconciliation/ledger/monthly/${month}/issue`, {
      method: "POST",
    }) as Promise<MonthlyReport>,
  getReconciliationPeriodDetail: (facilityId: string, periodId: string) =>
    apiFetch(`/facilities/${facilityId}/reconciliation/ledger/periods/${periodId}`) as Promise<{
      facility_id: string;
      period: LedgerPeriod;
      items: import("@/pages/facility/facility.types").LedgerOpenItem[];
    }>,
  closeReconciliationPeriod: (facilityId: string, periodId: string) =>
    apiFetch(`/facilities/${facilityId}/reconciliation/ledger/periods/${periodId}/close`, {
      method: "POST",
    }) as Promise<LedgerPeriod>,
  getReconciliationPayments: (facilityId: string, params?: { search?: string; amount?: number; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.search) q.set("search", params.search);
    if (params?.amount != null) q.set("amount", String(params.amount));
    if (params?.limit) q.set("limit", String(params.limit));
    const qs = q.toString() ? `?${q}` : "";
    return apiFetch(`/facilities/${facilityId}/reconciliation/payments${qs}`) as Promise<{
      facility_id: string;
      total: number;
      payments: ReconOpenPayment[];
    }>;
  },
  runReconciliation: (facilityId: string, accountId?: string) =>
    apiFetch(`/facilities/${facilityId}/reconciliation/run${accountId ? `?account_id=${accountId}` : ""}`, {
      method: "POST",
    }) as Promise<Record<string, unknown>>,
  createReconciliationMatch: (
    facilityId: string,
    body: {
      bank_transaction_ids: string[];
      payment_record_ids: string[];
      group_key?: string;
      notes?: string;
      create_alias?: boolean;
    },
  ) =>
    apiFetch(`/facilities/${facilityId}/reconciliation/matches`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }) as Promise<{ created: number; matches: ReconMatch[]; alias: ReconAliasInfo }>,
  confirmReconciliationMatch: (facilityId: string, matchId: string) =>
    apiFetch(`/facilities/${facilityId}/reconciliation/matches/${matchId}/confirm`, {
      method: "POST",
    }) as Promise<ReconMatch & { alias: ReconAliasInfo }>,
  rejectReconciliationMatch: (facilityId: string, matchId: string, note?: string) =>
    apiFetch(`/facilities/${facilityId}/reconciliation/matches/${matchId}/reject`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ note: note || null }),
    }) as Promise<ReconMatch>,
  uploadPaymentTape: (id: string, file: File, snapshotDate?: string, replace?: boolean) =>
    apiUploadAdmin(`/facilities/${id}/upload/payment-tape`, file, {
      ...(snapshotDate ? { snapshot_date: snapshotDate } : {}),
      ...(replace ? { replace: "true" } : {}),
    }),
  uploadSchedule: (id: string, file: File, snapshotDate?: string, replace?: boolean) =>
    apiUploadAdmin(`/facilities/${id}/upload/schedule`, file, {
      ...(snapshotDate ? { snapshot_date: snapshotDate } : {}),
      ...(replace ? { replace: "true" } : {}),
    }),
  uploadCustomer: (id: string, file: File, snapshotDate?: string, replace?: boolean) =>
    apiUploadAdmin(`/facilities/${id}/upload/customer`, file, {
      ...(snapshotDate ? { snapshot_date: snapshotDate } : {}),
      ...(replace ? { replace: "true" } : {}),
    }),
  uploadCollateral: (id: string, file: File, snapshotDate?: string, replace?: boolean) =>
    apiUploadAdmin(`/facilities/${id}/upload/collateral`, file, {
      ...(snapshotDate ? { snapshot_date: snapshotDate } : {}),
      ...(replace ? { replace: "true" } : {}),
    }),
  // Facility-scoped run-pipeline was removed (2026-07-22): a book ingest is always
  // book-scoped and cascades to every ACTIVE facility. Use runPipelineBook.
  // Borrower access (allowlist)
  listBorrowerAccess: (borrowerId: string) => apiFetch(`/borrowers/${borrowerId}/access`),
  grantBorrowerAccess: (borrowerId: string, email: string) =>
    apiFetch(`/borrowers/${borrowerId}/access`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    }),
  revokeBorrowerAccess: (borrowerId: string, email: string) =>
    apiFetch(`/borrowers/${borrowerId}/access/${encodeURIComponent(email)}`, { method: "DELETE" }),

  // Granular permissions
  listBorrowerPermissions: (borrowerId: string) => apiFetch(`/borrowers/${borrowerId}/permissions`),
  grantBorrowerPermission: (
    borrowerId: string,
    // `anonymized: true` (HU-066) switches the PERSON to anonymous on the book,
    // which covers every grant they hold there. Read permissions only: 422 for
    // a write grant, 409 when the person already writes on the book.
    body: { email: string; permission: string; deal_id?: string; anonymized?: boolean },
  ) =>
    apiFetch(`/borrowers/${borrowerId}/permissions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  revokeBorrowerPermission: (borrowerId: string, permissionId: string) =>
    apiFetch(`/borrowers/${borrowerId}/permissions/${permissionId}`, { method: "DELETE" }),
  /** HU-066: a person reads the whole book anonymously, or not at all. 409 when
   *  switching it on for somebody who uploads to or manages the book. */
  setBorrowerAnonymity: (borrowerId: string, email: string, anonymized: boolean) =>
    apiFetch(`/borrowers/${borrowerId}/anonymity`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, anonymized }),
    }),

  // Borrower self — returns their own permissions
  getMyPermissions: () => apiFetch(`/borrower/me/permissions`),

  // Borrower team management (requires manage_access permission)
  // `GET /borrower/me/book-facilities` used to feed the team page's scope
  // picker. A scoped grant names a DEAL since D4, so the picker reads
  // `listDeals()` — which D5 opened to a `manage_access` holder for exactly
  // this — and this client method went with its last consumer. The endpoint
  // itself stays; nothing here serves it.
  getTeamPermissions: () => apiFetch(`/borrower/me/team`),
  grantTeamPermission: (body: { email: string; permission: string; deal_id?: string; anonymized?: boolean }) =>
    apiFetch(`/borrower/me/team`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  revokeTeamPermission: (permissionId: string) =>
    apiFetch(`/borrower/me/team/${permissionId}`, { method: "DELETE" }),
  setTeamAnonymity: (email: string, anonymized: boolean) =>
    apiFetch(`/borrower/me/team/anonymity`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, anonymized }),
    }),

  // Admin
  cancelPipelineRun: (runId: string, reason?: string) =>
    apiFetch(`/admin/pipeline-runs/${runId}/cancel`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason: reason ?? null }),
    }),
  getMaintenanceMode: () => apiFetch(`/admin/maintenance`),
  setMaintenanceMode: (reason?: string) =>
    apiFetch(`/admin/maintenance`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason: reason ?? null }),
    }),
  clearMaintenanceMode: () => apiFetch(`/admin/maintenance`, { method: "DELETE" }),
  listAdminPipelines: () => apiFetch(`/admin/pipelines`),
};
